import React, { useEffect, useState } from 'react';
import { FiRefreshCw, FiSave, FiPlay, FiActivity, FiRotateCcw } from 'react-icons/fi';
import api from '../services/api';
import '../components/Button.css';

export default function ShopifyOrdersSync() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState({
    shop_domain: '',
    client_id: '',
    client_secret: '',
    enabled: false,
    poll_minutes: 5,
    woo_ready_status: 'afgerond',
    default_vat_rate: 0.21,
  });
  const [mappings, setMappings] = useState([]);
  const [lastRun, setLastRun] = useState(null);
  const [backfilling, setBackfilling] = useState(false);
  const [repairing, setRepairing] = useState(false);
  const [retryingId, setRetryingId] = useState(null);

  const fetchConfig = async () => {
    const res = await api.get('/shopify-orders/config');
    setConfig(res.data || null);
    setForm((f) => ({
      ...f,
      shop_domain: res.data?.shop_domain || '',
      client_id: res.data?.client_id || '',
      client_secret: res.data?.has_client_secret ? '••••••••••••' : '',
      enabled: !!res.data?.enabled,
      poll_minutes: res.data?.poll_minutes ?? 5,
      woo_ready_status: res.data?.woo_ready_status || 'afgerond'
    }));
  };

  const fetchMappings = async () => {
    const res = await api.get('/shopify-orders/mappings?limit=50');
    setMappings(Array.isArray(res.data) ? res.data : []);
  };

  useEffect(() => {
    (async () => {
      try {
        await fetchConfig();
        await fetchMappings();
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        shop_domain: form.shop_domain,
        client_id: form.client_id,
        client_secret: form.client_secret,
        enabled: !!form.enabled,
        poll_minutes: parseInt(form.poll_minutes, 10) || 5,
        woo_ready_status: form.woo_ready_status || 'afgerond',
        default_vat_rate: (() => {
          const v = parseFloat(form.default_vat_rate);
          if (Number.isNaN(v)) return 0.21;
          return Math.min(1, Math.max(0, v));
        })(),
      };
      await api.post('/shopify-orders/config', payload);
      await fetchConfig();
      alert('Configuratie opgeslagen.');
    } catch (e) {
      alert('Opslaan mislukt: ' + (e.response?.data?.error || e.message));
    } finally {
      setSaving(false);
    }
  };

  const handleRunNow = async () => {
    if (!window.confirm('Nu import uitvoeren (paid orders → Woo)?')) return;
    setRunning(true);
    try {
      const res = await api.post('/shopify-orders/run');
      setLastRun(res.data);
      await fetchMappings();
      const r = res.data || {};
      if (r.skipped && r.reason === 'already_running') {
        alert(
          'Import overgeslagen: er draaide al een import (bijv. automatische poll of dubbele klik). Wacht een paar seconden en probeer opnieuw.'
        );
        return;
      }
      if (r.skipped && r.reason === 'disabled') {
        alert('Import staat uit (niet ingeschakeld in config). Zet de koppeling aan en sla op.');
        return;
      }
      const checked = r.checked ?? 0;
      const tail =
        checked === 0 && !r.skipped
          ? '\n\nEr waren geen nieuwe betaalde Shopify-orders in het tijdvenster sinds de vorige poll (zie "created_at_min" in Laatste run). Dat is normaal als er niets te importeren valt. Eén order opnieuw: gebruik de knop in de mapping-tabel.'
          : '';
      alert(
        `Klaar: ${r.imported || 0} geïmporteerd, ${r.skippedExisting || 0} overgeslagen, ${r.failed || 0} fouten` +
          (r.retry_checked ? ` (${r.retry_checked} eerdere fouten opnieuw geprobeerd)` : '') +
          `\n(${checked} order(s) in Shopify-venster bekeken)` +
          tail
      );
    } catch (e) {
      alert('Run mislukt: ' + (e.response?.data?.error || e.message));
    } finally {
      setRunning(false);
    }
  };

  const handleRetryOne = async (m) => {
    if (!m.shopify_order_id) return;
    if (m.woocommerce_order_id) {
      alert('Deze mapping heeft al een Woo-order.');
      return;
    }
    if (!window.confirm(`Order ${m.shopify_order_number || m.shopify_order_id} (Shopify id ${m.shopify_order_id}) opnieuw naar Woo importeren?`)) return;
    setRetryingId(m.id);
    try {
      const res = await api.post('/shopify-orders/import-by-id', { shopify_order_id: String(m.shopify_order_id) });
      await fetchMappings();
      alert(`Geïmporteerd als Woo order ${res.data?.woocommerce_order_id ?? '?'}`);
    } catch (e) {
      alert(e.response?.data?.error || e.message);
    } finally {
      setRetryingId(null);
    }
  };

  const handleBackfillPakbon = async () => {
    if (!window.confirm('Shopify shop domain toevoegen aan bestaande Woo orders? (nodig voor pakbon-link)')) return;
    setBackfilling(true);
    try {
      const res = await api.post('/shopify-orders/backfill-shop-domain');
      alert(res.data.error || `Klaar: ${res.data.updated || 0} orders bijgewerkt (van ${res.data.total || 0})`);
    } catch (e) {
      alert('Backfill mislukt: ' + (e.response?.data?.error || e.message));
    } finally {
      setBackfilling(false);
    }
  };

  const handleRepairMappings = async (mappingRow = null) => {
    const one = mappingRow?.shopify_order_id;
    const msg = one
      ? `Koppeling repareren voor Shopify ${mappingRow.shopify_order_number || one}?`
      : 'Recente mappings controleren en herkoppelen waar het Woo-order-ID niet klopt?';
    if (!window.confirm(msg)) return;
    setRepairing(true);
    try {
      const res = await api.post('/shopify-orders/repair-mappings', one ? { shopify_order_id: String(one) } : { limit: 100 });
      await fetchMappings();
      const d = res.data || {};
      const parts = [
        `${d.checked ?? 0} gecontroleerd`,
        d.repaired ? `${d.repaired} hersteld` : null,
        d.refreshed ? `${d.refreshed} bijgewerkt` : null,
        d.ok ? `${d.ok} ok` : null,
        d.not_found ? `${d.not_found} niet gevonden in Woo` : null,
        d.conflict ? `${d.conflict} conflict` : null,
        d.error ? `${d.error} fout(en)` : null,
      ].filter(Boolean);
      let alertMsg = parts.join(', ');
      if (Array.isArray(d.details) && d.details.length) {
        const lines = d.details
          .slice(0, 8)
          .map((x) => {
            if (x.status === 'repaired') {
              return `${x.shopify_order_number || x.shopify_order_id}: Woo ${x.previous_woocommerce_order_id || '?'} → ${x.woocommerce_order_id}`;
            }
            if (x.status === 'not_found') {
              return `${x.shopify_order_number || x.shopify_order_id}: geen Woo-order gevonden`;
            }
            return `${x.shopify_order_number || x.shopify_order_id || '?'}: ${x.status}${x.error ? ` (${x.error})` : ''}`;
          });
        alertMsg += `\n\n${lines.join('\n')}`;
      }
      alert(alertMsg || 'Klaar.');
    } catch (e) {
      alert('Repareren mislukt: ' + (e.response?.data?.error || e.message));
    } finally {
      setRepairing(false);
    }
  };

  if (loading) return <div className="loading">Laden...</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
        <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <FiActivity /> Shopify → Woo orders
        </h2>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button className="btn btn-secondary" onClick={async () => { await fetchConfig(); await fetchMappings(); }} disabled={saving || running}>
            <FiRefreshCw /> Ververs
          </button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            <FiSave /> {saving ? 'Opslaan...' : 'Opslaan'}
          </button>
        </div>
      </div>

      <div className="wc-section" style={{ padding: '1rem' }}>
        <p style={{ color: '#666', marginTop: 0 }}>
          Polling loopt elke <strong>5 minuten</strong> op de server (als enabled). Import: Shopify paid → Woo (processing). TT push: Woo “afgerond” → Shopify fulfillment tracking.
          Orders die eerder faalden (zonder Woo-order) worden bij elke run opnieuw per Shopify-ID opgehaald, zodat je na aanpassingen in Shopify (bijv. SKU) alsnog kunt importeren.
        </p>
        <p style={{ color: '#64748b', fontSize: '0.92rem', marginBottom: 0 }}>
          <strong>Run nu</strong> importeert alleen orders in Shopify met <code>created_at</code> in het venster sinds de vorige run (met kleine overlap). Staat er <strong>0 bekeken</strong>, dan was er in dat venster niets nieuws te importeren — geen fout. Eén specifieke order: gebruik <strong>Opnieuw importeren</strong> op de regel.
        </p>
        <p style={{ color: '#666', marginTop: 0, fontSize: '0.9rem' }}>
          Installeer <code>inkoop-shopify-pakbon.php</code> in <code>wp-content/mu-plugins/</code> voor een &quot;Print Shopify pakbon&quot;-link in WooCommerce orders.
          Pakbon-productnamen volgen Shopify-vertalingen (Translate &amp; Adapt): de custom app moet scope <code>read_translations</code> hebben (optioneel <code>read_locales</code>); vertaal producttitels per publicatielocale in Shopify.
        </p>

        <div className="form" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <div className="form-group">
            <label>Shopify domein</label>
            <input value={form.shop_domain} onChange={(e) => setForm({ ...form, shop_domain: e.target.value })} placeholder="jouwshop.myshopify.com" />
          </div>
          <div className="form-group">
            <label>Client ID</label>
            <input value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} placeholder="client_id" />
          </div>
          <div className="form-group">
            <label>Client Secret</label>
            <input type="password" value={form.client_secret} onChange={(e) => setForm({ ...form, client_secret: e.target.value })} placeholder={config?.has_client_secret ? '(opgeslagen)' : 'client_secret'} />
          </div>
          <div className="form-group">
            <label>Woo status “gereed”</label>
            <input value={form.woo_ready_status} onChange={(e) => setForm({ ...form, woo_ready_status: e.target.value })} placeholder="afgerond" />
          </div>
          <div className="form-group">
            <label>Fallback btw voor Woo-import (decimaal)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              max="1"
              value={form.default_vat_rate}
              onChange={(e) => setForm({ ...form, default_vat_rate: e.target.value })}
            />
            <small style={{ color: '#888' }}>
              Alleen als Shopify geen bruikbare <code>tax_lines</code> op een regel teruggeeft: dan dit tarief (bijv. <strong>0.21</strong>). Normaal gebruikt de import het btw-bedrag per regel uit Shopify (elk land/tarief).
            </small>
          </div>
          <div className="form-group">
            <label>Polling (minuten)</label>
            <input type="number" min="1" max="60" value={form.poll_minutes} onChange={(e) => setForm({ ...form, poll_minutes: e.target.value })} />
            <small style={{ color: '#888' }}>Scheduler draait elke 5 min; deze waarde wordt gebruikt voor overlap/last-run.</small>
          </div>
          <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input type="checkbox" checked={!!form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />
            <label style={{ margin: 0 }}>Enabled</label>
          </div>
        </div>

        <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button className="btn btn-secondary" onClick={handleBackfillPakbon} disabled={backfilling} title="Voegt _shopify_shop_domain toe aan bestaande Woo orders zodat de pakbon-link werkt">
            {backfilling ? 'Bezig...' : 'Backfill pakbon-link'}
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => handleRepairMappings()}
            disabled={repairing || running}
            title="Zoekt in Woo op Shopify-meta als het opgeslagen Woo-order-ID niet klopt"
          >
            {repairing ? 'Bezig...' : 'Koppelingen repareren'}
          </button>
          <button className="btn btn-secondary" onClick={handleRunNow} disabled={running}>
            <FiPlay /> {running ? 'Bezig...' : 'Run nu'}
          </button>
        </div>

        {lastRun && (
          <div style={{ marginTop: '1rem', background: '#f5f5f5', padding: '0.75rem', borderRadius: 8 }}>
            <strong>Laatste run:</strong> {JSON.stringify(lastRun)}
          </div>
        )}
      </div>

      <div className="wc-section" style={{ padding: '1rem' }}>
        <h3 style={{ marginTop: 0 }}>Laatste mappings</h3>
        <div className="table-container">
          <table className="data-table" style={{ fontSize: '0.9rem' }}>
            <thead>
              <tr>
                <th>Shopify</th>
                <th>Woo</th>
                <th>Woo status</th>
                <th>TT</th>
                <th>Laatste fout</th>
                <th>Updated</th>
                <th style={{ width: '1%' }}>Actie</th>
              </tr>
            </thead>
            <tbody>
              {mappings.length === 0 ? (
                <tr><td colSpan="7" style={{ textAlign: 'center', color: '#666' }}>Nog geen mappings</td></tr>
              ) : mappings.map((m) => (
                <tr key={m.id}>
                  <td>
                    <div>{m.shopify_order_number || m.shopify_order_id}</div>
                    {!m.woocommerce_order_id && m.shopify_order_id ? (
                      <div style={{ fontSize: '0.75rem', color: '#888' }}>REST id {m.shopify_order_id}</div>
                    ) : null}
                  </td>
                  <td>
                    {m.woocommerce_order_id ? (
                      <>
                        <div>#{m.woocommerce_order_number || m.woocommerce_order_id}</div>
                        {m.woocommerce_order_number &&
                        String(m.woocommerce_order_number) !== String(m.woocommerce_order_id) ? (
                          <div style={{ fontSize: '0.75rem', color: '#888' }}>id {m.woocommerce_order_id}</div>
                        ) : null}
                      </>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td>{m.woocommerce_status || '-'}</td>
                  <td>{m.tracking_number ? `${m.tracking_company || ''} ${m.tracking_number}` : '-'}</td>
                  <td style={{ color: m.last_error ? '#d9534f' : '#666' }}>{m.last_error || '-'}</td>
                  <td>{m.updated_at ? new Date(m.updated_at).toLocaleString('nl-NL') : '-'}</td>
                  <td>
                    {!m.woocommerce_order_id && m.shopify_order_id ? (
                      <button
                        type="button"
                        className="btn btn-secondary"
                        style={{ padding: '0.25rem 0.5rem', fontSize: '0.8rem' }}
                        disabled={retryingId === m.id || running}
                        title="Haalt de order opnieuw uit Shopify en probeert Woo aan te maken"
                        onClick={() => handleRetryOne(m)}
                      >
                        <FiRotateCcw /> {retryingId === m.id ? '…' : 'Opnieuw'}
                      </button>
                    ) : m.last_error && m.shopify_order_id ? (
                      <button
                        type="button"
                        className="btn btn-secondary"
                        style={{ padding: '0.25rem 0.5rem', fontSize: '0.8rem' }}
                        disabled={repairing || running}
                        title="Zoekt de juiste Woo-order op Shopify-meta"
                        onClick={() => handleRepairMappings(m)}
                      >
                        <FiRotateCcw /> {repairing ? '…' : 'Repareer'}
                      </button>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

